'use client';

import React, { useState, useCallback, useEffect, ReactNode } from 'react';
import { ConsentStatus, ConsentStorageAdapter, EConsentStatus } from '../types';
import { CookieConsentContext } from './CookieConsentContext';

interface CookieConsentProviderProps {
    children: ReactNode;
    skipBanner?: boolean;
    storageAdapter: ConsentStorageAdapter;
    onEnableAnalytics?: () => void;
    onDisableAnalytics?: () => void;
}

export function CookieConsentProvider({
    children,
    skipBanner = false,
    storageAdapter,
    onEnableAnalytics = () => { },
    onDisableAnalytics = () => { },
}: CookieConsentProviderProps) {
    const [showBanner, setShowBanner] = useState(false);
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);
    const [consentStatus, setConsentStatus] = useState<ConsentStatus>(null);
    const [hasUserConsented, setHasUserConsented] = useState(false);

    useEffect(() => {
        const status = storageAdapter.getConsentStatus();
        const hasConsented = storageAdapter.hasUserConsented();

        setConsentStatus(status);
        setHasUserConsented(hasConsented);

        if (!hasConsented && !skipBanner) {
            setShowBanner(true);
        }
    }, [skipBanner]);

    const acceptAll = useCallback(() => {
        storageAdapter.grantConsent();
        onEnableAnalytics();
        setConsentStatus(EConsentStatus.GRANTED);
        setHasUserConsented(true);
        setShowBanner(false);
        setShowSettingsDialog(false);
    }, [onEnableAnalytics, storageAdapter]);

    const acceptOnlyNecessaryCookies = useCallback(() => {
        storageAdapter.denyConsent();
        onDisableAnalytics();
        setConsentStatus(EConsentStatus.DENIED);
        setHasUserConsented(true);
        setShowBanner(false);
        setShowSettingsDialog(false);
    }, [onDisableAnalytics, storageAdapter]);

    const openSettings = useCallback(() => {
        setShowSettingsDialog(true);
    }, []);

    const closeSettings = useCallback(() => {
        setShowSettingsDialog(false);
    }, []);

    const saveSettings = useCallback((enableAnalytics: boolean) => {
        if (enableAnalytics) {
            storageAdapter.grantConsent();
            onEnableAnalytics();
            setConsentStatus(EConsentStatus.GRANTED);
        } else {
            storageAdapter.denyConsent();
            onDisableAnalytics();
            setConsentStatus(EConsentStatus.DENIED);
        }
        setHasUserConsented(true);
        setShowBanner(false);
        setShowSettingsDialog(false);
    }, [onDisableAnalytics, onEnableAnalytics, storageAdapter]);

    const dismissBanner = useCallback(() => {
        setShowBanner(false);
    }, []);

    const value = {
        showBanner,
        showSettingsDialog,
        consentStatus,
        hasUserConsented,
        acceptAll,
        acceptOnlyNecessaryCookies,
        openSettings,
        closeSettings,
        saveSettings,
        dismissBanner,
    };

    return (
        <CookieConsentContext.Provider value={value}>
            {children}
        </CookieConsentContext.Provider>
    );
}
